import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  IconButton,
  Menu,
  Grid,
  Card,
  CardContent,
  CardActions,
  CardActionArea,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Check as CheckIcon,
  MoreVert as MoreVertIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as ViewingIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { 
  StyledDrawer, 
  DrawerContent, 
  MenuHeader,
  scrollbarStyles 
} from '../shared/StyledComponents';
import { GGMenuItem } from '../Menus';
import { popoverStyles, fantasyTheme } from '../../theme/fantasyTheme';
import { getBattlemapById } from '../../services/battlemapService';

const theme = fantasyTheme;

const MapCard = styled(Card)(({ theme, isViewing, isActive }) => ({
  borderRadius: theme.shape.borderRadius,
  backgroundColor: isViewing ? 'rgba(218, 165, 32, 0.12)' : 'rgba(0, 0, 0, 0.2)',
  border: isViewing 
    ? `2px solid ${theme.palette.secondary.main}` 
    : '2px solid transparent',
  transition: 'all 0.2s ease-in-out',
  position: 'relative',
  overflow: 'visible',
  '&:hover': {
    backgroundColor: isViewing 
      ? 'rgba(218, 165, 32, 0.15)' 
      : 'rgba(218, 165, 32, 0.05)',
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
  },
}));

const MapPreview = styled(Box)(({ theme }) => ({
  width: '100%',
  height: 100,
  overflow: 'hidden',
  borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
  backgroundColor: 'rgba(0, 0, 0, 0.3)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
}));

const ActiveBadge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 8,
  right: 8,
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  padding: '4px 8px',
  borderRadius: '12px',
  background: 'linear-gradient(135deg, rgba(52, 73, 94, 0.95) 0%, rgba(44, 62, 80, 0.95) 100%)',
  border: `1px solid ${theme.palette.secondary.main}`,
  zIndex: 2,
  '& .MuiSvgIcon-root': {
    color: '#FFD700',
    fontSize: '0.9rem',
  },
  '& .MuiTypography-root': {
    color: theme.palette.secondary.main,
    fontFamily: 'MedievalSharp',
    fontSize: '0.65rem',
    fontWeight: 600,
    lineHeight: 1,
  },
}));

const ViewingBadge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 8,
  left: 8,
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  padding: '4px 8px',
  borderRadius: '12px',
  background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.9) 0%, rgba(25, 118, 210, 0.9) 100%)',
  border: `1px solid rgba(255, 255, 255, 0.3)`,
  zIndex: 2,
  '& .MuiSvgIcon-root': {
    color: '#fff',
    fontSize: '0.9rem',
  },
  '& .MuiTypography-root': {
    color: '#fff',
    fontFamily: 'MedievalSharp',
    fontSize: '0.65rem',
    fontWeight: 600,
    lineHeight: 1,
  },
}));

const MapManagerDrawer = ({
  isOpen,
  onClose,
  maps,
  currentMapId,
  viewingMapId,
  isCreator,
  isMapLoading,
  onViewMap,
  onCreateMap,
  onEditMap,
  onDuplicateMap,
  onDeleteMap,
}) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedMapForMenu, setSelectedMapForMenu] = useState(null);

  const handleMenuOpen = (event, mapId) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedMapForMenu(mapId);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedMapForMenu(null);
  };

  const handleCardClick = (mapId) => {
    if (mapId !== viewingMapId) {
      onViewMap(mapId);
    }
  };

  const handleEditClick = (event, mapId) => {
    event.stopPropagation();
    const map = maps.find(m => m.id === mapId);
    if (map) {
      onEditMap(map);
    }
  };

  const getBattlemapPreview = (battlemapId) => {
    const battlemap = getBattlemapById(battlemapId);
    if (battlemap && battlemap.thumbnail) {
      return (
        <img
          src={battlemap.thumbnail}
          alt={battlemap.name}
          loading="lazy"
          decoding="async"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      );
    }
    // Default placeholder for no battlemap
    return (
      <Typography 
        sx={{ 
          color: 'text.secondary', 
          fontSize: '0.75rem', 
          fontFamily: 'MedievalSharp',
          textAlign: 'center',
          px: 2,
        }}
      >
        No Battlemap
      </Typography>
    );
  };

  return (
    <>
      <StyledDrawer anchor="left" open={isOpen} onClose={onClose}>
        <DrawerContent>
          <MenuHeader>
            <Typography>Map Manager</Typography>
            {isCreator && (
              <Tooltip title="Create New Map">
                <IconButton 
                  onClick={onCreateMap}
                  size="small"
                  sx={{ color: 'secondary.main' }}
                >
                  <AddIcon />
                </IconButton>
              </Tooltip>
            )}
          </MenuHeader>

          <Box sx={{ 
            p: 2, 
            overflowY: 'auto', 
            height: 'calc(100% - 60px)',
            ...scrollbarStyles 
          }}>
            {isMapLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {maps && maps.length > 0 ? (
                  <Grid container spacing={2}>
                    {maps.map((map) => {
                      const isViewing = map.id === viewingMapId;
                      const isActive = map.id === currentMapId;
                      
                      return (
                        <Grid item xs={12} sm={6} key={map.id}>
                          <MapCard isViewing={isViewing} isActive={isActive}>
                            <CardActionArea 
                              onClick={() => handleCardClick(map.id)}
                              disabled={!isCreator}
                            >
                              <MapPreview>
                                {getBattlemapPreview(map.battlemapId)}
                                {isActive && (
                                  <ActiveBadge>
                                    <CheckIcon />
                                    <Typography>Active</Typography>
                                  </ActiveBadge>
                                )}
                                {isViewing && !isActive && (
                                  <ViewingBadge>
                                    <ViewingIcon />
                                    <Typography>Viewing</Typography>
                                  </ViewingBadge>
                                )}
                              </MapPreview>
                              <CardContent sx={{ py: 1.5, px: 2 }}>
                                <Typography
                                  sx={{
                                    fontFamily: 'MedievalSharp',
                                    fontSize: '0.95rem',
                                    fontWeight: 600,
                                    textAlign: 'center',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {map.name}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: 'text.secondary',
                                    textAlign: 'center',
                                    display: 'block',
                                    fontSize: '0.7rem',
                                  }}
                                >
                                  {map.gridWidth || 20} × {map.gridHeight || 20} grids
                                </Typography>
                              </CardContent>
                            </CardActionArea>
                            
                            {isCreator && (
                              <CardActions sx={{ 
                                justifyContent: 'center', 
                                py: 0.5,
                                borderTop: '1px solid rgba(218, 165, 32, 0.1)',
                              }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="secondary"
                                  startIcon={<EditIcon fontSize="small" />}
                                  onClick={(e) => handleEditClick(e, map.id)}
                                  sx={{ 
                                    fontFamily: 'MedievalSharp',
                                    fontSize: '0.75rem',
                                  }}
                                >
                                  Edit
                                </Button>
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleMenuOpen(e, map.id)}
                                  sx={{ ml: 'auto' }}
                                >
                                  <MoreVertIcon fontSize="small" color="primary" />
                                </IconButton>
                              </CardActions>
                            )}
                          </MapCard>
                        </Grid>
                      );
                    })}
                  </Grid>
                ) : (
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography sx={{ 
                      fontFamily: 'MedievalSharp', 
                      color: 'text.secondary', 
                      fontSize: '0.9rem',
                      mb: 2,
                    }}>
                      {isCreator 
                        ? "No maps available. Create your first map!"
                        : "No maps available. Ask the DM to create maps."}
                    </Typography>
                    {isCreator && (
                      <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={onCreateMap}
                        sx={{ fontFamily: 'MedievalSharp' }}
                      >
                        Create First Map
                      </Button>
                    )}
                  </Box>
                )}
              </>
            )}
          </Box>
        </DrawerContent>
      </StyledDrawer>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        slotProps={popoverStyles(theme)}
      >
        <GGMenuItem 
          onClick={() => {
            onDuplicateMap(selectedMapForMenu);
            handleMenuClose();
          }}
          icon={<CopyIcon fontSize="small" />}
          label="Duplicate"
        />

        {maps.length > 1 && (
          <GGMenuItem 
            onClick={() => {
              onDeleteMap(selectedMapForMenu);
              handleMenuClose();
            }}
            icon={<DeleteIcon fontSize="small" />}
            label="Delete"
          />
        )}
      </Menu>
    </>
  );
};

export default MapManagerDrawer;
